import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaArrowRight } from "react-icons/fa";
import { FiShoppingBag } from "react-icons/fi";
import { MdVerified } from "react-icons/md";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { regionLabel } from "../data/regions";
import "./FeaturedStores.css";

// Each store gets a stable colour from its name (used when it has no cover photo)
const COLORS = ["#c97a2e", "#5b21e6", "#0f766e", "#be185d", "#1d4ed8", "#4d7c0f", "#b91c1c", "#334155"];
const colorOf = (name = "") => { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0; return COLORS[h % COLORS.length]; };
const slug = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const place = (c) => [c.city, c.state ? regionLabel(slug(c.state)) : ""].filter(Boolean).join(", ") || "Nigeria";

const OPEN_STORE = "/dashboard/profile";

export default function FeaturedStores() {
  const { isLoggedIn } = useAuth();
  const [stores, setStores] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const rail = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: true });

  useEffect(() => {
    let live = true;
    api("/companies/featured?limit=12").then((d) => live && setStores(d.companies || [])).catch(() => {}).finally(() => live && setLoaded(true));
    return () => { live = false; };
  }, []);

  const measure = useCallback(() => {
    const el = rail.current; if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure, stores, loaded]);
  const scroll = (dir) => { const el = rail.current; if (el) el.scrollBy({ left: dir * Math.max(260, el.clientWidth * 0.8), behavior: "smooth" }); };

  const openStore = isLoggedIn ? OPEN_STORE : `/login?next=${encodeURIComponent(OPEN_STORE)}`;

  return (
    <section className="fs-section">
      <div className="container">
        <div className="fs-head">
          <div className="fs-head-l">
            <span className="fs-eyebrow">Official seller stores</span>
            <h2 className="fs-title">Featured Stores &amp; Dealerships</h2>
            <p className="fs-sub">Shop directly from verified Nigerian business storefronts with dedicated inventory</p>
          </div>
          <div className="fs-actions">
            <Link to="/companies" className="fs-btn fs-btn-light">View All Stores <FaArrowRight /></Link>
            <Link to={openStore} className="fs-btn fs-btn-dark"><FiShoppingBag /> Open Your Store</Link>
          </div>
        </div>

        <div className="fs-stage">
          {!edge.start && <button type="button" className="fs-arrow fs-arrow-l" onClick={() => scroll(-1)} aria-label="Previous"><FaChevronLeft /></button>}

          <div className="fs-rail" ref={rail} onScroll={measure}>
            {!loaded && [0, 1, 2].map((i) => <div key={i} className="fs-card fs-skel" />)}

            {loaded && stores.map((c) => {
              const color = colorOf(c.name);
              return (
                <article className="fs-card" key={c.username}>
                  <Link to={`/seller/${c.username}`} className="fs-cover" style={{ background: c.cover ? `${color} url(${c.cover}) center/cover` : color }} aria-label={`${c.name} storefront`} />
                  <span className="fs-logo" style={{ background: color }}>
                    {c.logo ? <img src={c.logo} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <FiShoppingBag />}
                  </span>
                  <div className="fs-body">
                    <h3 className="fs-name" title={c.name}>
                      <Link to={`/seller/${c.username}`}>{c.name}</Link>
                      {c.verified && <MdVerified className="fs-verified" title="ID verified" />}
                    </h3>
                    <div className="fs-info">
                      <b>{c.listings} Listing{c.listings === 1 ? "" : "s"}</b>
                      <span>{place(c)}</span>
                    </div>
                    <Link to={`/seller/${c.username}`} className="fs-visit">Visit Storefront</Link>
                  </div>
                </article>
              );
            })}

            {loaded && stores.length === 0 && (
              <Link to={openStore} className="fs-card fs-empty">
                <span className="fs-empty-i"><FiShoppingBag /></span>
                <strong>Your store could be here</strong>
                <span>Business accounts with live listings are featured on the home page. Set up your store in minutes.</span>
                <em>Open Your Store <FaArrowRight /></em>
              </Link>
            )}
          </div>

          {!edge.end && <button type="button" className="fs-arrow fs-arrow-r" onClick={() => scroll(1)} aria-label="Next"><FaChevronRight /></button>}
        </div>
      </div>
    </section>
  );
}
